/**
 * AppNavbar - Main navigation sidebar component for Fleet Management
 * The navigation structure is defined in NavigationItems.tsx for easy maintenance.
 */

import { ActionIcon, Collapse, Group, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import { IconHelp } from '@tabler/icons-react';
import { ProtectedComponent } from '@/features/auth/components/ProtectedComponent';
import { ThemeToggle } from '@/ui/components/ThemeToggle';
import { UserProfile } from './UserProfile';
import { useFormattedUser, useLogoutRedirect } from '@/features/auth/hooks/auth.hooks';
import { navigationItems, NavItem } from "@/ui/layout/components/NavigationItems.tsx";
import { NavButton } from "@/ui/layout/components/NavButton.tsx";
import { useNavigation } from "@/ui/layout/hooks/useNavigation.ts";
import { SearchBarWithSuggestions } from "@/features/search/components/SearchBarWithSuggestions.tsx";
import { useParams } from "@tanstack/react-router";
import { useSystemCustomerQuery } from "@customers/api/customerQueries.ts";
import { HamburgerIcon } from "@/assets/CadwellIcons.tsx";

interface AppNavbarProps {
  /** Whether the desktop navigation is currently expanded */
  desktopOpened: boolean;
  /** Function to toggle the desktop navigation state */
  toggleDesktop: () => void;
  /** Whether the mobile navigation is currently open */
  mobileOpened: boolean;
  /** Function to close the mobile navigation */
  closeMobile: () => void;
}

export const AppNavbar = ({desktopOpened, toggleDesktop, mobileOpened, closeMobile}: AppNavbarProps) => {
  const theme = useMantineTheme();
  const formattedUser = useFormattedUser();
  const logout = useLogoutRedirect();

  // Get systemId from route params if on a system detail page
  const params = useParams({strict: false});
  const systemId = 'systemId' in params ? (params as { systemId: string }).systemId : undefined;

  // Fetch customer assignment for context-aware highlighting
  const {data: systemCustomer, isLoading: isLoadingCustomerAssignment} = useSystemCustomerQuery(
    systemId ?? '',
  );

  // Create navigation context for context-aware highlighting
  const navigationContext = {
    hasCustomerAssignment: !!systemCustomer?.customer,
    isLoadingCustomerAssignment: isLoadingCustomerAssignment && !!systemId,
  };

  const {expandedSections, toggleSection, getItemActiveState} = useNavigation(navigationContext);

  const navContainerStyle = {
    backgroundColor: 'var(--mantine-color-navbar)',
    color: 'var(--mantine-color-text)',
    borderRight: `1px solid var(--mantine-color-border)`,
    borderTopRightRadius: theme.radius.lg,
    overflow: 'hidden auto' as const,
    height: mobileOpened ? '100vh' : '100%',
    ...(mobileOpened && {
      position: 'fixed' as const,
      width: '100vw',
    }),
  };


  /**
   * Recursively renders navigation items with proper role-based protection.
   * Handles both collapsed (icon-only) and expanded views.
   */
  const onNavigate = mobileOpened ? closeMobile : undefined;

  const renderNavItems = (items: NavItem[], collapsed = false) =>
    items.map(item => {
      const active = getItemActiveState(item);
      const expanded = expandedSections[item.label];

      const content = collapsed ? (
        <NavButton
          item={item}
          collapsed
          isActive={active}
          onToggleSection={() => toggleSection(item.label)}
          getChildActiveState={getItemActiveState}
          onNavigate={onNavigate}
        />
      ) : (
        <Stack key={item.label} gap={0}>
          <NavButton
            item={item}
            isActive={active}
            isExpanded={expanded}
            onToggleSection={() => toggleSection(item.label)}
            onNavigate={onNavigate}
          />
          {item.children && (
            <Collapse in={expanded}>
              <Stack gap="xs" pl="md" pt="xs">
                {item.children.map(child => {
                  const childActive = getItemActiveState(child);
                  const childContent = (
                    <NavButton
                      key={child.label}
                      item={child}
                      isChild
                      isActive={childActive}
                      onNavigate={onNavigate}
                    />
                  );

                  return child.requiredRole ? (
                    <ProtectedComponent key={child.label} requiredRole={child.requiredRole}>
                      {childContent}
                    </ProtectedComponent>
                  ) : childContent;
                })}
              </Stack>
            </Collapse>
          )}
        </Stack>
      );

      return item.requiredRole ? (
        <ProtectedComponent key={item.label} requiredRole={item.requiredRole}>
          {content}
        </ProtectedComponent>
      ) : content;
    });

  // Collapsed desktop view: Show only icons with tooltips
  if (!desktopOpened && !mobileOpened) {
    return (
      <Stack style={navContainerStyle}>
        <Stack p="md" align="center">
          <ActionIcon
            onClick={toggleDesktop}
            variant="subtle"
            size="lg"
            aria-label="Toggle navigation"
          >
            <HamburgerIcon size={24}/>
          </ActionIcon>
          {renderNavItems(navigationItems, true)}
        </Stack>
      </Stack>
    );
  }

  // Expanded view: Show full navigation with labels and sections
  return (
    <Stack style={navContainerStyle}>
      <Group p="md" justify="space-between">
        <ActionIcon
          onClick={toggleDesktop}
          variant="subtle"
          size="lg"
          aria-label="Toggle navigation"
          visibleFrom='md'
        >
          <HamburgerIcon size={24}/>
        </ActionIcon>

        {mobileOpened && (
          <Stack w='100%'>
            <Group justify='space-between' w='95%'>
              <UserProfile
                user={formattedUser}
                onLogout={logout}
                showOnMobile={true}
              />
              <Group gap="xs">
                <Tooltip label="Help">
                  <ActionIcon
                    component="a"
                    href="/help/"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    aria-label="help-documentation"
                  >
                    <IconHelp size={20}/>
                  </ActionIcon>
                </Tooltip>
                <ThemeToggle showOnMobile={true}/>
              </Group>
            </Group>
            <SearchBarWithSuggestions size="sm"/>
          </Stack>
        )}
      </Group>

      <Stack pt={0} px="md">
        {renderNavItems(navigationItems)}
      </Stack>
    </Stack>
  );
};